import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFESSIONI = [
  { id: 'medico', label: 'Medico' },
  { id: 'veterinario', label: 'Veterinario' },
  { id: 'naturopata', label: 'Naturopata' },
  { id: 'operatore_olistico', label: 'Operatore olistico' },
  { id: 'erborista_parafarmacia', label: 'Erborista / parafarmacia' },
  { id: 'informatore_farmaceutico', label: 'Informatore farmaceutico' },
  { id: 'marketer_influencer', label: 'Marketer / influencer' },
];

/**
 * Passaggio obbligato al primo ingresso: professione e numero WhatsApp.
 *
 * La professione filtra i feedback e i materiali che ha senso mostrare, e
 * personalizza le comunicazioni. Il numero non è un consenso da spuntare: il
 * canale fa parte della collaborazione e i promotori ci sono tutti dentro, si
 * esce scrivendo STOP. Qui si conferma soltanto che il numero è quello giusto,
 * e chi lo trova già scritto va avanti in un secondo.
 */
export default function PrimoAccessoPage() {
  const { user, setUser } = useAuth();
  const [professione, setProfessione] = useState(user?.profile?.professional_type || '');
  const [telefono, setTelefono] = useState('');
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    // Numero già noto dal CRM: si propone così com'è, da correggere se serve.
    axios.get(`${API}/me/whatsapp`)
      .then((r) => {
        const d = r.data || {};
        setTelefono(d.conversazione?.telefono || d.telefono_suggerito || '');
      })
      .catch(() => {});
  }, []);

  const salva = async () => {
    if (!professione) {
      toast.error('Scegli la professione per continuare.');
      return;
    }
    setSalvo(true);
    try {
      const resp = await axios.post(`${API}/me/onboarding`, {
        professional_type: professione,
        telefono: telefono.trim() || null,
      });
      setUser((prev) => ({ ...prev, profile: { ...prev.profile, ...resp.data } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Non è stato possibile salvare. Riprova.');
    } finally {
      setSalvo(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#efe8da] flex items-center justify-center p-4" data-testid="primo-accesso">
      <Card className="w-full max-w-lg rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <h1 className="font-heading text-2xl font-bold text-[#12100c]">Benvenuto nel portale</h1>
          <p className="text-sm text-[#4a4238] mt-1 mb-6">
            Due informazioni e poi non te le chiediamo più.
          </p>

          <div className="mb-6">
            <Label className="text-[#12100c] font-medium text-sm">Come lavori</Label>
            <p className="text-xs text-[#4a4238] mt-0.5 mb-2">
              Serve a mostrarti i casi e i materiali dei colleghi che fanno il tuo stesso lavoro.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PROFESSIONI.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProfessione(p.id)}
                  className={`text-left text-sm px-3 py-2.5 rounded-xl border transition-colors ${
                    professione === p.id
                      ? 'border-[#12100c] bg-[#12100c] text-white'
                      : 'border-gray-200 text-[#12100c] hover:border-[#12100c]/40'
                  }`}
                  data-testid={`professione-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <Label htmlFor="telefono" className="text-[#12100c] font-medium text-sm flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4" /> Il tuo numero WhatsApp
            </Label>
            <p className="text-xs text-[#4a4238] mt-0.5 mb-2">
              È il numero che risulta nei nostri contatti: correggilo se non è più quello.
              Ti scriviamo poche cose e solo se utili: una vendita andata a buon fine, un
              contenuto nuovo. Quando non le vuoi più, rispondi STOP e smettiamo.
            </p>
            <Input
              id="telefono"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="+39 333 1234567"
              className="rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
              data-testid="primo-accesso-telefono"
            />
          </div>

          <Button
            onClick={salva}
            disabled={salvo}
            className="w-full bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full h-11 font-medium"
            data-testid="primo-accesso-salva"
          >
            {salvo && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Entra nel portale
          </Button>
          <p className="mt-3 text-center text-xs text-[#5b5346]">
            Il numero resta a Biounica e non viene ceduto a nessuno.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
